import { useState, useCallback, useRef, useEffect } from "react"
import { Check, Copy } from "lucide-react"
import { cn } from "@shared/lib/utils"
import { Button, type ButtonProps } from "./button"

export interface CopyButtonProps {
  /** Text to copy to clipboard */
  value: string
  /** Display mode */
  variant?: "icon" | "inline" | "button"
  /** Visible label (for inline/button modes) */
  label?: string
  /** Feedback text (default: "Copied!") */
  copiedLabel?: string
  /** Reset delay in ms (default: 2000) */
  timeout?: number
  /** Button variant (only for button mode) */
  buttonVariant?: ButtonProps["variant"]
  /** Button size (only for button/icon mode) */
  buttonSize?: ButtonProps["size"]
  className?: string
}

export function CopyButton({
  value,
  variant = "icon",
  label,
  copiedLabel = "Copied!",
  timeout = 2000,
  buttonVariant = "outline",
  buttonSize,
  className,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  const handleCopy = useCallback(async () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // silently fail
    }
    setCopied(true)
    timeoutRef.current = window.setTimeout(() => {
      setCopied(false)
      timeoutRef.current = null
    }, timeout)
  }, [value, timeout])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    }
  }, [])

  if (variant === "icon") {
    return (
      <Button
        variant="ghost"
        size={buttonSize ?? "icon"}
        className={cn("h-8 w-8 shrink-0", className)}
        onClick={handleCopy}
        title="Copy to clipboard"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
    )
  }

  if (variant === "inline") {
    return (
      <button
        onClick={handleCopy}
        className={cn(
          "inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors",
          className,
        )}
        title="Copy to clipboard"
      >
        <span className="truncate">{copied ? copiedLabel : (label || value)}</span>
        <span className="shrink-0">
          {copied ? (
            <Check className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </span>
      </button>
    )
  }

  // variant === "button"
  return (
    <Button
      variant={buttonVariant}
      size={buttonSize ?? "sm"}
      className={cn("gap-2", className)}
      onClick={handleCopy}
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
      {copied ? copiedLabel : (label || "Copy")}
    </Button>
  )
}
